import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Zap, Upload, Trash2, Loader2, CalendarDays, TrendingUp, TrendingDown,
  Building2, ChevronDown, Leaf, MapPin, Lightbulb, LineChart as LineIcon, ListOrdered, ArrowLeftRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PowerTable, colorMapFor } from "@/components/power/PowerShared";
import MonthComparison from "@/components/power/MonthComparison";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const ID_MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

const PERIODS = [
  { value: "monthly", label: "Bulanan" },
  { value: "daily", label: "Harian" },
  { value: "weekly", label: "Mingguan" },
];

/* ---------- helpers ---------- */
const fmtNum = (v, dec = 0) =>
  v == null ? "—" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: dec });

function fmtEnergy(kwh) {
  if (kwh == null) return { v: "—", u: "" };
  const abs = Math.abs(kwh);
  if (abs >= 1e6) return { v: (kwh / 1e6).toLocaleString("id-ID", { maximumFractionDigits: 2 }), u: "GWh" };
  if (abs >= 1e3) return { v: (kwh / 1e3).toLocaleString("id-ID", { maximumFractionDigits: 1 }), u: "MWh" };
  return { v: kwh.toLocaleString("id-ID", { maximumFractionDigits: 0 }), u: "kWh" };
}

const fmtDateID = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${ID_MONTHS[m - 1]} ${y}`;
};

function isoWeekLabel(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const isoYear = d.getUTCFullYear();
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const week = 1 + Math.round(((d - jan4) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return `${ID_MONTHS[m - 1]} ${y}`;
};

function aggregate(items, period, keys) {
  if (period === "daily") {
    return items.map((it) => ({ period: it.date, ...Object.fromEntries(keys.map((k) => [k, it.values?.[k] ?? null])) }));
  }
  const buckets = new Map();
  for (const it of items) {
    const label = period === "weekly" ? isoWeekLabel(it.date) : monthLabel(it.date.slice(0, 7));
    const sortKey = period === "weekly" ? isoWeekLabel(it.date) : it.date.slice(0, 7);
    if (!buckets.has(sortKey)) buckets.set(sortKey, { period: label, __sort: sortKey });
    const b = buckets.get(sortKey);
    for (const k of keys) {
      const v = it.values?.[k];
      if (v != null) b[k] = (b[k] || 0) + v;
    }
  }
  return [...buckets.values()].sort((a, b) => (a.__sort < b.__sort ? -1 : 1));
}

/* Delta of each key: last month vs previous month if >= 2 months, else last vs previous day */
function computeDeltas(items, keys) {
  if (items.length < 2) return { deltas: {}, label: "" };
  const months = [...new Set(items.map((it) => it.date.slice(0, 7)))].sort();
  let curItems, prevItems, label;
  if (months.length >= 2) {
    const cur = months[months.length - 1];
    const prev = months[months.length - 2];
    curItems = items.filter((it) => it.date.startsWith(cur));
    prevItems = items.filter((it) => it.date.startsWith(prev));
    label = `vs. ${monthLabel(prev)}`;
  } else {
    curItems = [items[items.length - 1]];
    prevItems = [items[items.length - 2]];
    label = "vs. hari sebelumnya";
  }
  const sum = (arr, k) => arr.reduce((s, it) => s + (it.values?.[k] ?? 0), 0);
  const deltas = {};
  for (const k of keys) {
    const p = sum(prevItems, k);
    if (p > 0) deltas[k] = ((sum(curItems, k) - p) / p) * 100;
  }
  const pTot = keys.reduce((s, k) => s + sum(prevItems, k), 0);
  const cTot = keys.reduce((s, k) => s + sum(curItems, k), 0);
  const total = pTot > 0 ? ((cTot - pTot) / pTot) * 100 : null;
  return { deltas, total, label };
}

/* ---------- small components ---------- */
const DeltaBadge = ({ pct, label, className = "" }) =>
  pct == null ? null : (
    <div className={`flex items-center gap-1 text-xs font-semibold ${pct <= 0 ? "text-green-600" : "text-red-600"} ${className}`}>
      {pct <= 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
      {Math.abs(pct).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%
      {label && <span className="text-slate-400 font-medium">{label}</span>}
    </div>
  );

function KpiCard({ icon: Icon, iconClass, title, value, unit, sub, delta, deltaLabel, tint, testId }) {
  return (
    <div className={`bg-gradient-to-br ${tint} to-white rounded-xl border border-slate-200/60 p-5 kpi-shadow`} data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${iconClass}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="font-bold text-slate-700 text-xs leading-tight">{title}</div>
      </div>
      <div className="text-3xl font-black text-slate-900 tabular-nums">
        {value} <span className="text-base font-bold text-slate-500">{unit}</span>
      </div>
      {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
      {delta != null && <DeltaBadge pct={delta} label={deltaLabel} className="mt-2" />}
    </div>
  );
}

function Card({ icon: Icon, title, subtitle, actions, children, className = "", testId }) {
  return (
    <div className={`bg-white rounded-xl border border-slate-200/60 kpi-shadow p-5 ${className}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          {Icon && (
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-[#1e40af] flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4" />
            </div>
          )}
          <div>
            <div className="font-bold text-slate-800 text-sm">{title}</div>
            {subtitle && <div className="text-[11px] text-slate-400 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const sorted = [...payload].filter((p) => p.value != null).sort((a, b) => b.value - a.value);
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-3 text-xs max-h-72 overflow-y-auto min-w-[200px]">
      <div className="font-bold text-slate-800 mb-2">{label}</div>
      {sorted.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-6 py-0.5">
          <span className="flex items-center gap-1.5 text-slate-600">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            {p.dataKey}
          </span>
          <span className="font-semibold tabular-nums text-slate-800">{fmtNum(p.value)} kWh</span>
        </div>
      ))}
    </div>
  );
}

function Sparkline({ data, color }) {
  if (!data.length) return null;
  return (
    <div className="h-7 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
          <Line dataKey="v" stroke={color} strokeWidth={1.5} dot={false} type="monotone" isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ---------- page ---------- */
export default function PowerRSS() {
  const { isAdmin } = useAuth();
  const [allItems, setAllItems] = useState([]);
  const [keys, setKeys] = useState([]);
  const [period, setPeriod] = useState("daily");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [selected, setSelected] = useState(null); // null = all
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/power/rss");
      const items = res.data.items || [];
      setAllItems(items);
      setKeys(res.data.keys || []);
      if (items.length) {
        setRangeStart((s) => s || items[0].date);
        setRangeEnd((e) => e || items[items.length - 1].date);
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!allItems.length) return;
    const months = new Set(allItems.map((it) => it.date.slice(0, 7)));
    if (months.size >= 2) setPeriod((p) => (p === "daily" ? "monthly" : p));
  }, [allItems]);

  const items = useMemo(
    () => allItems.filter((it) => (!rangeStart || it.date >= rangeStart) && (!rangeEnd || it.date <= rangeEnd)),
    [allItems, rangeStart, rangeEnd]
  );

  const colorMap = useMemo(() => colorMapFor(keys), [keys]);
  const selectedKeys = useMemo(
    () => (selected == null ? keys : keys.filter((k) => selected.includes(k))),
    [selected, keys]
  );

  const series = useMemo(() => aggregate(items, period, keys), [items, period, keys]);

  const totals = useMemo(() => {
    const t = {};
    for (const k of keys) t[k] = items.reduce((s, it) => s + (it.values?.[k] ?? 0), 0);
    return t;
  }, [items, keys]);
  const grandTotal = keys.reduce((s, k) => s + (totals[k] || 0), 0);
  const avg = keys.length ? grandTotal / keys.length : 0;

  const highest = keys.length ? keys.reduce((a, b) => ((totals[a] || 0) >= (totals[b] || 0) ? a : b)) : null;
  const lowest = keys.length ? keys.reduce((a, b) => ((totals[a] || 0) <= (totals[b] || 0) ? a : b)) : null;

  const { deltas, total: totalDelta, label: deltaLabel } = useMemo(
    () => computeDeltas(items, keys),
    [items, keys]
  );

  const rangeLabel = rangeStart && rangeEnd ? `${fmtDateID(rangeStart)} – ${fmtDateID(rangeEnd)}` : "";
  const lastUpdated = useMemo(() => {
    const ts = allItems.map((it) => it.uploaded_at).filter(Boolean).sort();
    return ts.length ? new Date(ts[ts.length - 1]).toLocaleString("id-ID") : "—";
  }, [allItems]);

  const sparks = useMemo(() => {
    const out = {};
    for (const k of keys) out[k] = items.map((it) => ({ v: it.values?.[k] ?? null }));
    return out;
  }, [items, keys]);

  const toggleBuilding = (name) => {
    const cur = selected == null ? [...keys] : [...selected];
    const next = cur.includes(name) ? cur.filter((k) => k !== name) : [...cur, name];
    setSelected(next);
  };

  /* ---------- actions ---------- */
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/power/upload", fd);
      toast.success(`Upload berhasil — RSS: ${res.data.rss_rows} baris, TSS: ${res.data.tss_rows} baris tersimpan`);
      setSelected(null);
      setRangeStart(""); setRangeEnd("");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const deleteAll = async () => {
    if (!window.confirm("Hapus SEMUA data konsumsi RSS?")) return;
    try {
      const res = await api.delete("/power/rss");
      toast.success(`${res.data.deleted} data RSS dihapus`);
      setRangeStart(""); setRangeEnd("");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const deleteRow = async (date) => {
    if (!window.confirm(`Hapus data RSS tanggal ${date}?`)) return;
    try {
      await api.delete("/power/rss", { params: { date } });
      toast.success(`Data ${date} dihapus`);
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const gt = fmtEnergy(grandTotal);
  const av = fmtEnergy(avg);
  const hi = fmtEnergy(totals[highest]);
  const lo = fmtEnergy(totals[lowest]);
  const pctOf = (k) => (grandTotal ? ((totals[k] || 0) / grandTotal) * 100 : 0);

  return (
    <div className="space-y-6 pb-8" data-testid="power-rss-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#1e40af]">Energy Management</div>
          <h1 className="text-3xl font-black text-slate-900 mt-1">Power Consumption per Building</h1>
          <p className="text-slate-500 text-sm mt-1">Penggunaan daya listrik pada setiap gedung operasional MRT Jakarta</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="rss-date-range-btn" className="h-9 text-xs font-semibold text-slate-700">
                  <CalendarDays className="w-4 h-4 mr-2 text-[#1e40af]" />
                  {rangeLabel || "Pilih periode"}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-3 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rentang Tanggal</div>
                <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} data-testid="rss-range-start" className="h-9 text-xs" />
                <Input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} data-testid="rss-range-end" className="h-9 text-xs" />
              </PopoverContent>
            </Popover>
            <div className="text-right text-[11px] text-slate-400 leading-tight">
              <div>Terakhir diperbarui</div>
              <div className="font-semibold text-slate-600">{lastUpdated}</div>
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} data-testid="rss-file-input" />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="rss-upload-btn"
                className="h-9 bg-[#1e40af] hover:bg-[#1e3a8a] text-white text-xs font-semibold"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Excel
              </Button>
              <Button
                onClick={deleteAll}
                variant="outline"
                data-testid="rss-delete-all-btn"
                className="h-9 text-xs font-semibold text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus Semua
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          icon={Zap} iconClass="bg-blue-100 text-blue-600" tint="from-blue-50/80"
          title="Total Konsumsi Daya" value={gt.v} unit={gt.u}
          delta={totalDelta} deltaLabel={deltaLabel} testId="rss-kpi-total"
        />
        <KpiCard
          icon={Leaf} iconClass="bg-green-100 text-green-600" tint="from-green-50/80"
          title="Rata-rata per Gedung" value={av.v} unit={av.u}
          sub={keys.length ? `${keys.length} gedung` : ""} testId="rss-kpi-avg"
        />
        <KpiCard
          icon={Building2} iconClass="bg-purple-100 text-purple-600" tint="from-purple-50/80"
          title={`Gedung Tertinggi${highest ? ` — ${highest}` : ""}`} value={hi.v} unit={hi.u}
          sub={highest ? `(${pctOf(highest).toLocaleString("id-ID", { maximumFractionDigits: 1 })}% dari total)` : ""}
          testId="rss-kpi-highest"
        />
        <KpiCard
          icon={Building2} iconClass="bg-teal-100 text-teal-600" tint="from-teal-50/80"
          title={`Gedung Terendah${lowest ? ` — ${lowest}` : ""}`} value={lo.v} unit={lo.u}
          sub={lowest ? `(${pctOf(lowest).toLocaleString("id-ID", { maximumFractionDigits: 1 })}% dari total)` : ""}
          testId="rss-kpi-lowest"
        />
      </div>

      {/* Trend + totals side panel */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card
          icon={LineIcon}
          title="Tren Konsumsi Daya per Gedung"
          subtitle={rangeLabel ? `Periode ${rangeLabel}` : "Belum ada data"}
          className="xl:col-span-2"
          testId="rss-trend-card"
          actions={
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="rss-building-filter-btn" className="h-8 text-xs font-semibold">
                    Gedung ({selectedKeys.length}/{keys.length})
                    <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 p-3" data-testid="rss-building-filter">
                  <div className="flex items-center justify-between mb-2">
                    <button onClick={() => setSelected([...keys])} className="text-[11px] font-semibold text-[#1e40af] hover:underline" data-testid="rss-select-all">
                      Pilih semua
                    </button>
                    <button onClick={() => setSelected([])} className="text-[11px] font-semibold text-slate-500 hover:underline" data-testid="rss-clear-all">
                      Kosongkan
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                    {keys.map((name) => (
                      <label key={name} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer py-0.5">
                        <Checkbox
                          checked={selectedKeys.includes(name)}
                          onCheckedChange={() => toggleBuilding(name)}
                          data-testid={`rss-building-${name.replace(/\s+/g, "-")}`}
                        />
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorMap[name] }} />
                        {name}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="rss-period-filter">
                {PERIODS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => setPeriod(p.value)}
                    data-testid={`rss-period-${p.value}`}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      period === p.value ? "bg-[#1e40af] text-white" : "bg-white text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          }
        >
          {series.length && selectedKeys.length ? (
            <div className="h-[400px]" data-testid="rss-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 10, right: 16, bottom: 5, left: 14 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} minTickGap={20} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => fmtNum(v)}
                    width={72}
                    label={{ value: "Konsumsi Daya (kWh)", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#64748b", fontWeight: 600 } }}
                  />
                  <Tooltip content={<TrendTooltip />} />
                  {selectedKeys.map((k) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={colorMap[k]} strokeWidth={2} dot={{ r: 2.5, strokeWidth: 0, fill: colorMap[k] }} activeDot={{ r: 4 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[400px] flex items-center justify-center text-sm text-slate-400" data-testid="rss-chart-empty">
              {series.length ? "Pilih minimal satu gedung untuk menampilkan tren." : "Belum ada data — upload logsheet Excel untuk melihat tren."}
            </div>
          )}
        </Card>

        {/* Totals side panel */}
        <Card icon={ListOrdered} title={`Total Konsumsi${rangeLabel ? ` (${rangeLabel})` : ""}`} testId="rss-totals-panel">
          {grandTotal ? (
            <div className="overflow-y-auto max-h-[430px] pr-1">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-slate-400 uppercase text-[10px] tracking-wider">
                    <th className="text-left py-1.5 font-bold">Gedung</th>
                    <th className="text-right py-1.5 font-bold">kWh</th>
                    <th className="text-right py-1.5 font-bold">%</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr key={k} className="border-t border-slate-100">
                      <td className="py-1.5">
                        <span className="flex items-center gap-1.5 text-slate-700 font-medium">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colorMap[k] }} />
                          {k}
                        </span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums font-semibold text-slate-800">{fmtNum(totals[k])}</td>
                      <td className="py-1.5 text-right tabular-nums text-slate-500">
                        {pctOf(k).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">Belum ada data.</div>
          )}
        </Card>
      </div>

      {/* Building cards + insight */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card icon={Building2} title="Konsumsi Daya per Gedung" className="xl:col-span-2" testId="rss-building-cards">
          {grandTotal ? (
            <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-3">
              {keys.map((k) => {
                const e = fmtEnergy(totals[k]);
                return (
                  <div key={k} className="rounded-xl border border-slate-200/70 p-3 hover:shadow-md transition-shadow" data-testid={`rss-card-${k.replace(/\s+/g, "-")}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${colorMap[k]}1c`, color: colorMap[k] }}>
                        <Building2 className="w-3.5 h-3.5" />
                      </div>
                      <div className="text-[11px] font-bold text-slate-700 truncate">{k}</div>
                    </div>
                    <div className="text-lg font-black text-slate-900 tabular-nums">
                      {e.v} <span className="text-[11px] font-bold text-slate-400">{e.u}</span>
                    </div>
                    <DeltaBadge pct={deltas[k]} className="mt-0.5 !text-[11px]" />
                    <Sparkline data={sparks[k] || []} color={colorMap[k]} />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">Belum ada data.</div>
          )}
        </Card>

        <Card icon={Lightbulb} title="Insight & Ringkasan" testId="rss-insight-card">
          {grandTotal ? (
            <div className="space-y-3">
              {totalDelta != null && (
                <div className="flex gap-2.5 bg-slate-50/80 rounded-lg p-3">
                  {totalDelta <= 0 ? <TrendingDown className="w-4 h-4 mt-0.5 shrink-0 text-green-600" /> : <TrendingUp className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />}
                  <div className="text-xs">
                    <div className={`font-bold ${totalDelta <= 0 ? "text-green-600" : "text-red-600"}`}>
                      Total konsumsi daya {totalDelta <= 0 ? "turun" : "naik"} {Math.abs(totalDelta).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%
                    </div>
                    <div className="text-slate-600 mt-0.5 leading-relaxed">
                      Dibandingkan periode sebelumnya ({deltaLabel.replace("vs. ", "")}), total konsumsi daya seluruh gedung {totalDelta <= 0 ? "turun" : "naik"} sebesar {Math.abs(totalDelta).toLocaleString("id-ID", { maximumFractionDigits: 1 })}%.
                    </div>
                  </div>
                </div>
              )}
              {highest && (
                <div className="flex gap-2.5 bg-slate-50/80 rounded-lg p-3">
                  <TrendingUp className="w-4 h-4 mt-0.5 shrink-0 text-purple-600" />
                  <div className="text-xs">
                    <div className="font-bold text-purple-600">{highest} tertinggi</div>
                    <div className="text-slate-600 mt-0.5 leading-relaxed">
                      Gedung {highest} memiliki konsumsi daya tertinggi ({pctOf(highest).toLocaleString("id-ID", { maximumFractionDigits: 1 })}% dari total).
                    </div>
                  </div>
                </div>
              )}
              {lowest && (
                <div className="flex gap-2.5 bg-slate-50/80 rounded-lg p-3">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
                  <div className="text-xs">
                    <div className="font-bold text-blue-600">{lowest} terendah</div>
                    <div className="text-slate-600 mt-0.5 leading-relaxed">
                      Gedung {lowest} memiliki konsumsi daya terendah ({pctOf(lowest).toLocaleString("id-ID", { maximumFractionDigits: 1 })}% dari total).
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-2.5 bg-slate-50/80 rounded-lg p-3">
                <Leaf className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
                <div className="text-xs">
                  <div className="font-bold text-green-600">Efisiensi energi</div>
                  <div className="text-slate-600 mt-0.5 leading-relaxed">
                    Efisiensi energi tetap menjadi fokus utama untuk memastikan operasional MRT Jakarta yang lebih berkelanjutan.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">Belum ada data.</div>
          )}
        </Card>
      </div>

      {/* Month comparison */}
      <Card icon={ArrowLeftRight} title="Perbandingan Antar Bulan" subtitle="Bandingkan konsumsi dua bulan pilihan" testId="rss-cmp-card">
        <MonthComparison items={allItems} keys={keys} entityLabel="Gedung" testIdPrefix="rss" />
      </Card>

      {/* Data table */}
      <Card icon={CalendarDays} title={`Data Upload (${items.length} hari)`} testId="rss-table-card">
        <PowerTable
          items={items}
          keys={selectedKeys.length ? selectedKeys : keys}
          isAdmin={isAdmin}
          onDeleteRow={deleteRow}
          testId="rss-table"
        />
      </Card>
    </div>
  );
}
